import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
// import Toast from 'react-native-toast-message';
import { ToastContainer, toast } from 'react-toastify';
import TopGreenHeader from './screens/Common/TopGreenHeader';
import API_PATHS from './apiCalls/ApiPath';

const ManageFamilyScreen = ({ navigation }) => {
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [isFamilyHead, setIsFamilyHead] = useState(false);
  const [familyHeadId, setFamilyHeadId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ visible: false, memberUserId: null, memberName: '' });

  // ── Fetch family members every time screen opens ───────────────────────────
  useFocusEffect(
    useCallback(() => {
      fetchMembers();
    }, [])
  );

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const userId = await AsyncStorage.getItem('userId');
      const token = await AsyncStorage.getItem('token');

      const response = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.FETCH_FAMILY}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parentId: userId,
          }),
        }
      );
      const data = await response.json();

      if (data.Success === 1) {
        const familyHeadId = data.Data?.familyHeadId;
        const list = data.Data?.familyMembersList || [];

        setMembers(list);
        setFamilyHeadId(familyHeadId);
        // Current logged-in user is family head only if their userId matches familyHeadId
        setIsFamilyHead(userId === familyHeadId);
      } else {
        // Toast.show({ type: 'error', text1: data.Message || 'Failed to fetch family members' });
        toast.error(data.Message || 'Failed to fetch family members');
      }
    } catch (error) {
      console.error('fetchMembers error:', error);
      // Toast.show({ type: 'error', text1: 'Something went wrong while fetching members' });
      toast.error('Something went wrong while fetching members');
    } finally {
      setLoading(false);
    }
  };

  // ── Send Invitation ──────────────────────────────────────────────────────
  // Backend requires: { email, inviterName, inviterEmail }
  // inviterName/inviterEmail = the logged-in family head's own details,
  // NOT saved in AsyncStorage, so we fetch them fresh from the profile API.
  const handleSendInvitation = async () => {
    if (!email.trim()) {
      // Toast.show({ type: 'error', text1: 'Please enter an email address' });
      toast.error('Please enter an email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      // Toast.show({ type: 'error', text1: 'Please enter a valid email address' });
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setInviteLoading(true);
      const userId = await AsyncStorage.getItem('userId');
      const token = await AsyncStorage.getItem('token');

      // Step 1 — get the logged-in user's own name + email
      // (same API ProfileScreen.jsx already uses to fill its form fields)
      const profileResponse = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.PROFILE_API}/${userId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const profileData = await profileResponse.json();

      if (profileData.Success !== 1 || !profileData.Data?.response?.parent) {
        // Toast.show({ type: 'error', text1: 'Could not load your profile details' });
        toast.error('Could not load your profile details');
        setInviteLoading(false);
        return;
      }

      const parentData = profileData.Data.response.parent;
      const inviterName = parentData.name;
      const inviterEmail = parentData.email;

      // Step 2 — send the actual invite with inviter details attached
      const response = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.INVITE_FAMILY_MEMBER}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim(),
            inviterName: inviterName,
            inviterEmail: inviterEmail,
          }),
        }
      );
      const result = await response.json();

      if (result.Success === 1) {
        // Toast.show({ type: 'success', text1: result.Message || 'Invitation sent successfully!' });
        toast.success(result.Message || 'Invitation sent successfully');
        setEmail('');
      } else {
        // Toast.show({ type: 'error', text1: result.Message || 'Failed to send invitation' });
        toast.success(result.Message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('handleSendInvitation error:', error);
      // Toast.show({ type: 'error', text1: 'Something went wrong while sending invitation' });
      toast.error('Something went wrong while sending invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Delete Member (Family Head only) ────────────────────────────────────────
  // Backend requires the string-style userId (e.g. "DLC82437"), NOT the
  // numeric database id (e.g. 55). So we pass item.userId here, not item.id.
  const handleDeleteMember = (memberUserId, memberName) => {
    setConfirmModal({ visible: true, memberUserId, memberName });
  };

  const performDelete = async () => {
    const { memberUserId } = confirmModal;
    setConfirmModal({ visible: false, memberUserId: null, memberName: '' });

    try {
      const token = await AsyncStorage.getItem('token');

      const response = await fetch(
        `${API_PATHS.BASE_URL}${API_PATHS.REMOVE_FAMILY_MEMBER}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ memberId: memberUserId }),
        }
      );

      const result = await response.json();

      if (result.Success === 1) {
        fetchMembers();
      } else {
        toast.error(result.Message || 'Failed to remove member');
      }
    } catch (error) {
      console.error('handleDeleteMember error:', error);
    }
  };

  // ── Render Each Member ───────────────────────────────────────────────────────
  const renderMember = ({ item }) => {
    return (
      <View style={styles.memberCard}>

        {/* Avatar — Profile pic if available, else initials */}
        <View style={styles.avatarContainer}>
          {item.profilePic ? (
            <Image source={{ uri: item.profilePic }} style={styles.avatarImage} />
          ) : (
            <View style={styles.initialsBox}>
              <Text style={styles.initialsText}>
                {item.name?.charAt(0)?.toUpperCase() || '?'}
              </Text>
            </View>
          )}
        </View>

        {/* Name + Active Status */}
        <View style={styles.memberInfo}>
          <Text style={styles.memberName}>{item.name}</Text>
          <Text style={styles.memberEmail}>{item.email}</Text>

        </View>

        {/* Delete button — visible to family head, but not on their own row */}
        {isFamilyHead && item.userId !== familyHeadId && (
          <TouchableOpacity
            onPress={() => handleDeleteMember(item.userId, item.name)}
            style={styles.actionBtn}
          >
            <TrashIcon />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <TopGreenHeader
        Heading="Manage Family"
        isBackIcon={true}
        isNotification={false}
        navigation={navigation}
      />

      <FlatList
        data={members}
        keyExtractor={(item, index) => item.userId?.toString() || index.toString()}
        renderItem={renderMember}
        ListHeaderComponent={
          <>
            {/* Only family head can invite new members */}
            {isFamilyHead && (
              <>

                <Text style={styles.sectionTitle}>Invite Member</Text>

                <View style={styles.inviteCard}>
                  <Text style={styles.inputLabel}>EMAIL ADDRESS</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="example@family.com"
                    placeholderTextColor="#bbb"
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.inviteButton}
                    onPress={handleSendInvitation}
                    disabled={inviteLoading}
                  >
                    {inviteLoading ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={styles.inviteButtonText}>Send Invitation</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}


            <View style={styles.membersHeader}>
              <Text style={styles.sectionTitle}>Current Members</Text>
              <View style={styles.totalBadge}>
                <Text style={styles.totalText}>{members.length} Total</Text>
              </View>
            </View>

            {loading && (
              <ActivityIndicator size="large" color="#8BC34A" style={{ marginTop: 20 }} />
            )}
          </>
        }
        ListEmptyComponent={
          !loading && (
            <Text style={styles.emptyText}>No family members yet.</Text>
          )
        }
        contentContainerStyle={styles.listContent}
      />
      <Modal
        animationType="slide"
        transparent={true}
        visible={confirmModal.visible}
        onRequestClose={() => setConfirmModal({ visible: false, memberUserId: null, memberName: '' })}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalText}>
              Are you sure you want to remove {confirmModal.memberName} from your family?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={performDelete}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalButton}
                onPress={() => setConfirmModal({ visible: false, memberUserId: null, memberName: '' })}
              >
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <ToastContainer position="top-center" autoClose={3000} />
    </View>
  );
};

// ── Trash icon component (no image file needed) ──────────────────────────────
const TrashIcon = () => (
  <View style={trashStyles.wrapper}>
    <View style={trashStyles.lid} />
    <View style={trashStyles.handle} />
    <View style={trashStyles.body}>
      <View style={trashStyles.line} />
      <View style={trashStyles.line} />
      <View style={trashStyles.line} />
    </View>
  </View>
);

const trashStyles = StyleSheet.create({
  wrapper: {
    alignItems: 'center',
    width: 20,
    height: 24,
  },
  handle: {
    width: 8,
    height: 3,
    backgroundColor: '#aaa',
    borderRadius: 2,
    marginBottom: 1,
    position: 'absolute',
    top: 0,
  },
  lid: {
    width: 18,
    height: 3,
    backgroundColor: '#aaa',
    borderRadius: 2,
    marginTop: 4,
  },
  body: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderColor: '#aaa',
    borderRadius: 2,
    marginTop: 2,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingVertical: 2,
  },
  line: {
    width: 1.5,
    height: 10,
    backgroundColor: '#aaa',
    borderRadius: 1,
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  listContent: {
    paddingBottom: 30,
  },
  sectionLabel: {
    fontSize: 11,
    color: '#888',
    letterSpacing: 1.2,
    marginBottom: 2,
    paddingHorizontal: 16,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  sectionTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#111',
    paddingHorizontal: 16,
    marginTop: 24,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },

  // ── Invite Card ──
  inviteCard: {
    backgroundColor: '#fff',
    marginHorizontal: 14,
    marginTop: 14,
    marginBottom: 10,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  inputLabel: {
    fontSize: 11,
    color: '#888',
    letterSpacing: 0.8,
    marginBottom: 8,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 14,
    height: 48,
    fontSize: 15,
    color: '#333',
    marginBottom: 14,
    backgroundColor: '#fff',
  },
  inviteButton: {
    backgroundColor: '#8BC34A',
    borderRadius: 8,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inviteButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },

  // ── Members Header ──
  membersHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginTop: 4,
    marginBottom: 12,
  },
  totalBadge: {
    backgroundColor: '#e8f5d0',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 5,
  },
  totalText: {
    color: '#4a7c1b',
    fontSize: 13,
    fontWeight: '600',
  },

  // ── Member Card ──
  memberCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    marginHorizontal: 14,
    marginBottom: 10,
    borderRadius: 12,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },

  // ── Avatar ──
  avatarContainer: {
    width: 52,
    height: 52,
    borderRadius: 12,
    backgroundColor: '#c6e39e',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  initialsBox: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#b8d98a',
  },
  initialsText: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#3b6d11',
  },

  // ── Member Info ──
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111',
    marginBottom: 2,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  memberEmail: {
    fontSize: 12,
    color: '#888',
    marginBottom: 4,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  statusBadge: {
    alignSelf: 'flex-start',
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: '#d4edaa',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: '#3b6d11',
  },

  // ── Action Buttons ──
  actionBtn: {
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  emptyText: {
    textAlign: 'center',
    color: '#aaa',
    marginTop: 30,
    fontSize: 15,
  },

  // ── Confirm Modal ──
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    width: 300,
    padding: 20,
    backgroundColor: '#fff',
    borderRadius: 10,
    alignItems: 'center',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: '#333',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    padding: 10,
    marginHorizontal: 10,
    backgroundColor: '#8BC34A',
    alignItems: 'center',
    borderRadius: 5,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
  },
});

export default ManageFamilyScreen;